import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Clock } from "lucide-react";

const LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_equity-support/artifacts/114243fe_edi%20logo.png";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("checking");

    useEffect(() => {
        if (!sessionId) { setState("error"); return; }
        let active = true;
        let tries = 0;
        const poll = async () => {
            if (!active) return;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") { setState("paid"); return; }
                if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
            } catch (e) { /* retry */ }
            tries += 1;
            if (tries >= 8) { setState("timeout"); return; }
            setTimeout(poll, 2000);
        };
        poll();
        return () => { active = false; };
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-[#FBF7F0] text-[#14203A] font-sans flex flex-col" data-testid="payment-success-page">
            <header className="border-b border-[#eee3d0]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center">
                    <Link to="/"><img src={LOGO} alt="EDI Connect" className="h-14 w-auto" /></Link>
                </div>
            </header>
            <div className="flex-1 flex items-center justify-center px-4 py-20">
                <div className="max-w-lg w-full bg-white rounded-2xl border border-[#eee3d0] p-10 text-center">
                    {state === "checking" && (
                        <div data-testid="payment-checking">
                            <Loader2 className="h-12 w-12 text-[#C58B2B] mx-auto animate-spin" />
                            <h1 className="font-display text-2xl font-bold mt-6">Confirming your payment…</h1>
                            <p className="mt-3 text-[#3d4657]">This only takes a moment. Please don't close this page.</p>
                        </div>
                    )}
                    {state === "paid" && (
                        <div data-testid="payment-paid">
                            <div className="h-16 w-16 rounded-full bg-[#00A499]/10 flex items-center justify-center mx-auto"><CheckCircle2 className="h-9 w-9 text-[#00A499]" /></div>
                            <h1 className="font-display text-3xl font-bold mt-6">You're a member 🎉</h1>
                            <p className="mt-3 text-[#3d4657]">Welcome to EDI Connect. We've emailed your confirmation and next steps. Your founding membership is secured — this is a 12-month subscription, and you'll manage it in the EDI Connect app from launch in November.</p>
                            <Link to="/"><Button className="mt-8 bg-[#14203A] hover:bg-[#22304f] text-white rounded-full px-8" data-testid="payment-home-btn">Back to home</Button></Link>
                        </div>
                    )}
                    {state === "timeout" && (
                        <div data-testid="payment-timeout">
                            <Clock className="h-12 w-12 text-[#C58B2B] mx-auto" />
                            <h1 className="font-display text-2xl font-bold mt-6">Still processing</h1>
                            <p className="mt-3 text-[#3d4657]">Your payment is taking a little longer than usual. If you completed checkout, you'll receive a confirmation email shortly. Contact info@ediconnect-healthcare.com if you need help.</p>
                            <Link to="/"><Button className="mt-8 bg-[#14203A] hover:bg-[#22304f] text-white rounded-full px-8">Back to home</Button></Link>
                        </div>
                    )}
                    {state === "error" && (
                        <div data-testid="payment-error">
                            <div className="h-16 w-16 rounded-full bg-red-50 flex items-center justify-center mx-auto"><XCircle className="h-9 w-9 text-red-500" /></div>
                            <h1 className="font-display text-2xl font-bold mt-6">Payment not completed</h1>
                            <p className="mt-3 text-[#3d4657]">We couldn't confirm your payment. No charge has been taken if checkout was cancelled. Please try again or contact info@ediconnect-healthcare.com.</p>
                            <Link to="/"><Button className="mt-8 bg-[#14203A] hover:bg-[#22304f] text-white rounded-full px-8">Back to home</Button></Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
